/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PassdownLogProps {
  id: string;
  name: string;
  regionId: string;
  logDate: Date;
  body: string;
  requiresAcknowledgement: boolean;
  dateAcknowledged: Date | null;
  acknowledgedById: string | null;
  authorId: string | null;
  regionIdList: List<string> | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PassdownLogRecord extends Record<PassdownLogProps>({
  id: null as any,
  name: null as any,
  regionId: null as any,
  logDate: null as any,
  body: null as any,
  requiresAcknowledgement: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  authorId: null as any,
  regionIdList: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    regionId: string;
    logDate: string | number | Date;
    body: string;
    requiresAcknowledgement: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    authorId: string | null;
    regionIdList: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PassdownLogRecord {
    if (json == null) {
      throw new Error('PassdownLogRecord.fromJS: parameter cannot be null');
    }
    return new PassdownLogRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'PassdownLogJson.name'),
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogJson.regionId',
      ),
      logDate: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'logDate', '0001-01-01T00:00:00.00Z'),
          'PassdownLogJson.logDate',
        ),
      ),
      body: getFromOrThrow(json, (j) => j.body, 'PassdownLogJson.body'),
      requiresAcknowledgement: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
        'PassdownLogJson.requiresAcknowledgement',
      ),
      dateAcknowledged: json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null,
      acknowledgedById: json.acknowledgedById,
      authorId: json.authorId,
      regionIdList:
        json.regionIdList != null
          ? List<string>(json.regionIdList.map((o: any) => new String(o)))
          : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    regionId: string;
    logDate: string | number | Date;
    body: string;
    requiresAcknowledgement: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    authorId: string | null;
    regionIdList: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PassdownLogRecord, Error[]] {
    if (json == null) {
      throw new Error('PassdownLogRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PassdownLogRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'PassdownLogJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      logDate: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'logDate', '0001-01-01T00:00:00.00Z'),
              'PassdownLogJson.logDate',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      body: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.body, 'PassdownLogJson.body'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiresAcknowledgement: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
            'PassdownLogJson.requiresAcknowledgement',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () => (json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () => json.acknowledgedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionIdList: tryOrDefault(
        () =>
          json.regionIdList != null
            ? List<string>(json.regionIdList.map((o: any) => new String(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PassdownLogProps) {
    super(props);
    if (props == null) {
      throw new Error('PassdownLogRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PassdownLogViewModelBase implements PassdownLogProps {
  protected readonly _props: PassdownLogProps;
  private _json?: PassdownLogProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get logDate(): Date {
    return this._props.logDate;
  }
  get body(): string {
    return this._props.body;
  }
  get requiresAcknowledgement(): boolean {
    return this._props.requiresAcknowledgement;
  }
  get dateAcknowledged(): Date | null {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string | null {
    return this._props.acknowledgedById;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get regionIdList(): List<string> | null {
    return this._props.regionIdList;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PassdownLogProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        regionId: this._props.regionId,
        logDate: this._props.logDate,
        body: this._props.body,
        requiresAcknowledgement: this._props.requiresAcknowledgement,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        authorId: this._props.authorId,
        regionIdList: this._props.regionIdList,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPassdownLogEntities = (state: AppState) =>
  state.entityStores.passdownLog.entities;

export const selectPassdownLogById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLog.entities.get(id);

export const selectPassdownLogMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLog.meta.get(id);

export const selectPassdownLogAndMetadataById = (id: string) =>
  createSelector(
    selectPassdownLogById(id),
    selectPassdownLogMetadataById(id),
    (passdownLog, meta) => ({ passdownLog, meta }),
  );

export function selectPassdownLogEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPassdownLogEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PassdownLogRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPassdownLogEntitiesByName(name: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByLogDate(logDate: Date) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.logDate === logDate).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByBody(body: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.body === body).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByRequiresAcknowledgement(
  requiresAcknowledgement: boolean,
) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.requiresAcknowledgement === requiresAcknowledgement).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByAcknowledgedById(acknowledgedById: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectPassdownLogEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPassdownLogEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
